// src/pages/UserProfilePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Bell, Lock, Save, RefreshCw,
  Camera, Eye, EyeOff,
} from 'lucide-react';
import { profileAPI } from '../api/groupsApi';
import { useAuth } from '../contexts/AuthContext';
import NotificationsPanel from '../components/Profile/NotificationsPanel';
import './UserProfilePage.css';

const PROFILE_TABS = ['Profile', 'Notifications', 'Privacy & Settings', 'Security'];

const Toggle = ({ checked, onChange, label, description }) => (
  <label className="toggle-item">
    <div className="toggle-item__info">
      <span className="toggle-item__label">{label}</span>
      {description && <span className="toggle-item__desc">{description}</span>}
    </div>
    <div className="toggle-wrap">
      <input
        type="checkbox"
        className="toggle-input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-slider" />
    </div>
  </label>
);

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { user: authUser, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  // Profile state
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [settingsError, setSettingsError] = useState('');

  // Editable profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bio, setBio] = useState('');
  const [institution, setInstitution] = useState('');
  const [position, setPosition] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Password change
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const fetchData = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const [profileRes, settingsRes] = await Promise.all([
        profileAPI.getProfile(),
        profileAPI.getSettings(),
      ]);
      const u = profileRes.user;
      setProfile(u);
      setFirstName(u.first_name || '');
      setLastName(u.last_name || '');
      setBio(u.profile?.bio || '');
      setInstitution(u.profile?.institution || '');
      setPosition(u.profile?.position || '');
      setAvatarPreview(u.profile?.avatar || null);
      setSettings(settingsRes.profile);
    } catch (e) {
      setProfileError('Failed to load profile.');
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Profile save ──────────────────────────────────────────────────────────

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const formData = new FormData();
      formData.append('first_name', firstName);
      formData.append('last_name', lastName);
      if (avatarFile) formData.append('avatar', avatarFile);

      // Profile sub-fields go to settings endpoint
      await profileAPI.updateProfile({ first_name: firstName, last_name: lastName });
      await profileAPI.updateSettings({ bio, institution, position });

      setProfileSuccess('Profile updated successfully.');
      if (updateUser) updateUser({ first_name: firstName, last_name: lastName });
    } catch (e) {
      setProfileError(e.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Settings save ─────────────────────────────────────────────────────────

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      await profileAPI.updateSettings(settings);
      setSettingsSuccess('Settings saved.');
    } catch (e) {
      setSettingsError('Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Password change ───────────────────────────────────────────────────────

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    setPwLoading(true);
    try {
      await profileAPI.changePassword(oldPassword, newPassword, confirmPassword);
      setPwSuccess('Password changed. Please log in again.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/login'), 2000);
    } catch (e) {
      setPwError(
        e.response?.data?.errors?.old_password?.[0] ||
        e.response?.data?.detail ||
        'Failed to change password.'
      );
    } finally {
      setPwLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  if (loadingProfile) {
    return (
      <div className="profile-page profile-page--loading">
        <RefreshCw size={32} className="spinning" />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <h1 className="profile-page__title">My Profile</h1>

      {/* Tabs */}
      <div className="tabs">
        {PROFILE_TABS.map((tab, i) => (
          <button
            key={tab}
            className={`tab ${activeTab === i ? 'tab--active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {i === 0 && <User size={14} />}
            {i === 1 && <Bell size={14} />}
            {i === 2 && <Lock size={14} />}
            {i === 3 && <Lock size={14} />}
            {tab}
          </button>
        ))}
      </div>

      <div className="profile-page__content">
        {/* ── Tab 0: Profile ── */}
        {activeTab === 0 && (
          <form className="profile-form" onSubmit={handleSaveProfile}>
            {/* Avatar */}
            <div className="profile-form__avatar-section">
              <div className="profile-avatar">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="profile-avatar__img" />
                ) : (
                  <div className="profile-avatar__placeholder">
                    {(firstName || authUser?.first_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <label className="profile-avatar__upload">
                  <Camera size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={handleAvatarChange}
                  />
                </label>
              </div>
              <div className="profile-form__avatar-info">
                <p className="profile-form__name">
                  {firstName} {lastName}
                </p>
                <p className="profile-form__email">{profile?.email}</p>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={30}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={30}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Institution</label>
              <input
                className="form-input"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="e.g. MSU Chemistry Department"
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Position / Role</label>
              <input
                className="form-input"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Student, Research Scientist"
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="A short description about yourself…"
                maxLength={500}
              />
              <span className="form-hint">{bio.length}/500</span>
            </div>

            {profileError && <p className="form-error">{profileError}</p>}
            {profileSuccess && <p className="form-success">{profileSuccess}</p>}

            <button type="submit" className="btn btn--primary" disabled={savingProfile}>
              {savingProfile ? <RefreshCw size={14} className="spinning" /> : <Save size={14} />}
              {savingProfile ? 'Saving…' : 'Save Profile'}
            </button>
          </form>
        )}

        {/* ── Tab 1: Notifications ── */}
        {activeTab === 1 && (
          <div className="profile-page__notifications">
            <NotificationsPanel />
          </div>
        )}

        {/* ── Tab 2: Privacy & Settings ── */}
        {activeTab === 2 && settings && (
          <div className="settings-form">
            <section className="settings-section">
              <h3 className="settings-section__title">Privacy</h3>
              <div className="toggle-list">
                <Toggle
                  checked={settings.show_email}
                  onChange={(v) => handleSettingChange('show_email', v)}
                  label="Show email to group members"
                  description="Other members of your groups can see your email address"
                />
                <Toggle
                  checked={settings.show_groups}
                  onChange={(v) => handleSettingChange('show_groups', v)}
                  label="Show group memberships"
                  description="Display your group memberships on your public profile"
                />
                <Toggle
                  checked={settings.searchable}
                  onChange={(v) => handleSettingChange('searchable', v)}
                  label="Appear in user search"
                  description="Allow others to find you when inviting to groups"
                />
                <Toggle
                  checked={settings.allow_group_invitations}
                  onChange={(v) => handleSettingChange('allow_group_invitations', v)}
                  label="Allow group invitations"
                  description="Allow other users to send you group invitations"
                />
              </div>
            </section>

            <section className="settings-section">
              <h3 className="settings-section__title">Notification Preferences</h3>
              <div className="toggle-list">
                <Toggle
                  checked={settings.notify_group_invitations}
                  onChange={(v) => handleSettingChange('notify_group_invitations', v)}
                  label="Group invitations"
                  description="Notify when you receive a group invitation"
                />
                <Toggle
                  checked={settings.notify_role_changes}
                  onChange={(v) => handleSettingChange('notify_role_changes', v)}
                  label="Role changes"
                  description="Notify when your role in a group changes"
                />
                <Toggle
                  checked={settings.notify_resource_shared}
                  onChange={(v) => handleSettingChange('notify_resource_shared', v)}
                  label="Resource sharing"
                  description="Notify when a resource is shared with your group"
                />
                <Toggle
                  checked={settings.notify_member_activity}
                  onChange={(v) => handleSettingChange('notify_member_activity', v)}
                  label="Member activity"
                  description="Notify when members join or leave groups you admin"
                />
              </div>

              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label className="form-label">Notification Frequency</label>
                <select
                  className="form-select"
                  value={settings.notification_frequency}
                  onChange={(e) =>
                    handleSettingChange('notification_frequency', e.target.value)
                  }
                >
                  <option value="instant">Instant</option>
                  <option value="daily">Daily Digest</option>
                  <option value="weekly">Weekly Digest</option>
                  <option value="never">Never</option>
                </select>
              </div>
            </section>

            {settingsError && <p className="form-error">{settingsError}</p>}
            {settingsSuccess && <p className="form-success">{settingsSuccess}</p>}

            <button
              className="btn btn--primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? <RefreshCw size={14} className="spinning" /> : <Save size={14} />}
              {savingSettings ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        )}

        {/* ── Tab 3: Security ── */}
        {activeTab === 3 && (
          <form className="security-form" onSubmit={handleChangePassword}>
            <h2 className="settings-section__title">Change Password</h2>

            <div className="form-group">
              <label className="form-label">Current Password</label>
              <div className="password-input-wrap">
                <input
                  className="form-input"
                  type={showOld ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowOld((p) => !p)}
                >
                  {showOld ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="password-input-wrap">
                <input
                  className="form-input"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNew((p) => !p)}
                >
                  {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                className="form-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            {pwError && <p className="form-error">{pwError}</p>}
            {pwSuccess && <p className="form-success">{pwSuccess}</p>}

            <button
              type="submit"
              className="btn btn--primary"
              disabled={pwLoading || !oldPassword || !newPassword || !confirmPassword}
            >
              {pwLoading ? <RefreshCw size={14} className="spinning" /> : <Lock size={14} />}
              {pwLoading ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;
